
import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import App from './App';
import './index.css';
import { Toaster } from "@/components/ui/toaster";
import { supabase } from "@/integrations/supabase/client";
import { clearImageUrlCache } from '@/utils/imageUtils';
import { SessionContextProvider } from '@supabase/auth-helpers-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { logger } from '@/utils/logger';

// Create a query client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Initialize app services after render to avoid blocking initial load
const initializeAppServices = () => {
  // Run storage initialization after a brief delay to not block rendering
  setTimeout(async () => {
    try {
      logger.debug("Initializing storage access...");
      
      // Import the robust storage checker
      const { checkStorageAccess } = await import('@/utils/imageUtils');
      
      const accessResult = await checkStorageAccess();
      
      if (accessResult.success) {
        logger.debug("✅ Storage access verified:", accessResult.message);
      } else {
        logger.warn("⚠️ Storage access limited:", accessResult.message);
        logger.debug("Storage will use fallback mechanisms for image loading");
      }
      
      // Clear cache regardless of access status to ensure fresh state
      clearImageUrlCache();
      
    } catch (error) {
      logger.warn("Storage initialization completed with warnings:", error);
      logger.debug("Application will continue with fallback image handling");
    }
  }, 100);
};

ReactDOM.createRoot(document.getElementById('root') as HTMLElement).render(
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <SessionContextProvider supabaseClient={supabase}>
        <BrowserRouter>
          <App />
          <Toaster />
        </BrowserRouter>
      </SessionContextProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

// Initialize services after app renders
initializeAppServices();
